import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Save scroll position before route changes
    const handleScroll = () => {
      const position = window.scrollY;
      const timestamp = Date.now();
      const scrollData = {
        position: position,
        timestamp: timestamp,
        pathname: pathname
      };
      localStorage.setItem(`scroll_${pathname}`, JSON.stringify(scrollData));
    };

    // Handle page unload/close
    const handleBeforeUnload = () => {
      // Save current page position before unload
      const currentPosition = window.scrollY;
      const timestamp = Date.now();
      const scrollData = {
        position: currentPosition,
        timestamp: timestamp,
        pathname: pathname
      };
      localStorage.setItem(`scroll_${pathname}`, JSON.stringify(scrollData));
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Restore scroll position or scroll to top
    const savedData = localStorage.getItem(`scroll_${pathname}`);
    if (savedData) {
      try {
        const { position } = JSON.parse(savedData);
        window.scrollTo(0, position);
      } catch (e) {
        window.scrollTo(0, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save final position when unmounting
      const finalPosition = window.scrollY;
      const timestamp = Date.now();
      const scrollData = {
        position: finalPosition,
        timestamp: timestamp,
        pathname: pathname
      };
      localStorage.setItem(`scroll_${pathname}`, JSON.stringify(scrollData));
    };
  }, [pathname]);

  // Clear old scroll positions when they're no longer needed
  useEffect(() => {
    const STORAGE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days
    const MAX_POSITIONS = 500; // Maximum number of stored positions

    const cleanupScrollPositions = () => {
      const now = Date.now();
      const positions = [];

      // Collect all scroll position entries
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('scroll_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            positions.push({
              key,
              ...data
            });
          } catch (e) {
            // Remove invalid entries
            localStorage.removeItem(key);
          }
        }
      }

      // Sort by timestamp, newest first
      positions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      // Remove old or excess entries
      positions.forEach((position, index) => {
        if (index >= MAX_POSITIONS || now - (position.timestamp || 0) > STORAGE_EXPIRY) {
          localStorage.removeItem(position.key);
        }
      });
    };

    cleanupScrollPositions();
  }, [pathname]);

  return null;
}

export default ScrollToTop; 